import { inject, Injectable } from '@angular/core';
import { SubAppModes } from '@components/likut-clients/likut-clients.component';
import { compress, decompress } from 'lz-string';
import { BehaviorSubject, catchError, of, throwError } from 'rxjs';
import { Product2 } from '../Interfaces/all-products';
import { AllProducts, AllProductsLikut, DocSpecialProduct, ProductsMinimumQuantity } from '../Interfaces/SQL-morning-responses';
import { StoreService } from './store.service';

export type ProductMinimumQuantitySinglesAndPacks = {
	singles: number;
	packs: number;
	singlesBehavior: 'round' | 'onlyInitial' | null;
	packsBehavior: 'round' | 'onlyInitial' | null;
};

@Injectable({
	providedIn: 'root',
})
export class ProductsService {
	private _ss = inject(StoreService);

	productsMinimumQuantity: Record<string, ProductMinimumQuantitySinglesAndPacks> = {};

	historyPriceDictionary: Record<string, number> = {};

	allProducts$ = new BehaviorSubject<AllProducts[] | null>(null);
	allProductsLikut$ = new BehaviorSubject<AllProductsLikut[] | null>(null);

	constructor() {}

	filterOutOnlyProductsWithHistory(opts: { products: Product2[]; resetQTAswell?: boolean }): Product2[] {
		const { products, resetQTAswell } = opts;
		const filterFn = (product: Product2) => product.NumOfTimes > 0 && product.docquantity;
		if (resetQTAswell) {
			const finalProducts = [];
			for (const product of products) {
				product.qt = 0;
				if (filterFn(product)) {
					finalProducts.push(product);
				}
			}
			return finalProducts;
		}
		return products.filter(filterFn);
	}

	async getDocSpecialProductsHashMap(filterByDocType: number, isVanSale?: boolean): Promise<Record<string, DocSpecialProduct> | null> {
		let specialDocProductIDs: DocSpecialProduct[] | null;
		if (isVanSale) {
			specialDocProductIDs = await new Promise<DocSpecialProduct[] | null>(resolve => {
				this._ss
					.getDocSpecialProducts()
					.pipe(catchError(() => throwError(() => resolve(null))))
					.subscribe({
						next: res => {
							if (res !== 'ConnectionError' && res !== 'RequestError') {
								localStorage.setItem('DocSpecialProducts', compress(JSON.stringify(res.recordset)));
								return resolve(res.recordset);
							}

							return resolve(null);
						},
					});
			});
		} else {
			const ls = localStorage.getItem('DocSpecialProducts');
			specialDocProductIDs = ls ? JSON.parse(decompress(ls)) : ls;
		}

		if (!specialDocProductIDs) return null;

		const filteredWithDoctype = specialDocProductIDs.filter(special => special.DocType === filterByDocType);

		if (!filteredWithDoctype.length) return null;

		return filteredWithDoctype.reduce((prev, curr) => {
			prev[curr.ProductID.toLowerCase()] = curr;
			return prev;
		}, {} as Record<string, DocSpecialProduct>);
	}

	async getProductsMinimumQuantity(): Promise<void> {
		await new Promise<void>(resolve => {
			this._ss
				.getProductsMinimumQuantity()
				.pipe(catchError(() => of({ recordset: [] })))
				.subscribe({
					next: res => {
						let products: ProductsMinimumQuantity[] = [];
						if (typeof res !== 'string' && res.recordset?.length) {
							products = res.recordset;
							localStorage.setItem('ProductsMinimumQuantity', compress(JSON.stringify(products)));
						} else {
							const stored = localStorage.getItem('ProductsMinimumQuantity');
							if (stored) {
								products = JSON.parse(decompress(stored));
							}
						}

						this.calculateProductMinimumQuantities(products);
						resolve();
					},
				});
		});
	}

	calculateProductMinimumQuantities(data: ProductsMinimumQuantity[]) {
		data = data.filter(d => d.ClientID === this._ss.OneClientInfo.ClientId && d.Company === this._ss.OneClientInfo.company && this._ss.AgentIdConnected === d.AgentID);
		this.productsMinimumQuantity = data.reduce((prev, curr) => {
			const id = curr.ProductID.toLowerCase();
			prev[id] ||= { packsBehavior: null, singlesBehavior: null, packs: 0, singles: 0 };

			if (curr.QuantityType === '0') {
				prev[id].singles = curr.Quantity;
				prev[id].singlesBehavior = curr.Behavior === '0' ? 'onlyInitial' : 'round';
			}

			if (curr.QuantityType === '1') {
				prev[id].packs = curr.Quantity;
				prev[id].packsBehavior = curr.Behavior === '0' ? 'onlyInitial' : 'round';
			}

			return prev;
		}, {} as typeof this.productsMinimumQuantity);
	}

	buildHistoryPriceDictionary(products: Product2[]) {
		this.historyPriceDictionary = products.reduce((prev, curr) => {
			if (isFinite(+curr.DocNetPrice)) {
				prev[curr.ProductId] = +curr.DocNetPrice;
			}
			return prev;
		}, {});
	}

	cleanup() {
		this.historyPriceDictionary = {};
	}

	async getProducts(): Promise<AllProducts[]> {
		if (this.allProducts$.value?.length) return Promise.resolve(this.allProducts$.value);

		return new Promise<AllProducts[]>((resolve, reject) => {
			this._ss
				.GetAllProducts()
				.pipe(catchError(() => throwError(() => reject())))
				.subscribe({
					next: res => {
						if (typeof res === 'string') {
							return reject();
						}

						this.allProducts$.next(res.recordset);

						resolve(res.recordset);
					},
				});
		});
	}
	async getProductsWithoutAgent(mode: SubAppModes): Promise<AllProductsLikut[]> {
		if (this.allProductsLikut$.value?.length) return Promise.resolve(this.allProductsLikut$.value);

		return new Promise<AllProductsLikut[]>((resolve, reject) => {
			this._ss
				.getAllProductsWithoutAgent(mode)
				.pipe(catchError(() => throwError(() => reject())))
				.subscribe({
					next: res => {
						if (typeof res === 'string') {
							return reject();
						}

						this.allProductsLikut$.next(res.recordset);

						resolve(res.recordset);
					},
				});
		});
	}
}
